import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  Building2,
  CircleCheck,
  FolderKanban,
  Search,
  Users,
} from "lucide-react"
import { useMemo, useState } from "react"

import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Progress } from "@/components/ui/progress"
import { adminOverviewQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/admin/agencies")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(adminOverviewQueryOptions()),
  head: () => ({
    meta: [{ title: "Agencies · Service Portal admin" }],
  }),
  component: AdminAgenciesPage,
})

function AdminAgenciesPage() {
  const { data } = useSuspenseQuery(adminOverviewQueryOptions())
  const [query, setQuery] = useState("")
  const agencies = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return data.agencies.filter(
      (agency) =>
        !normalized ||
        agency.name.toLowerCase().includes(normalized) ||
        agency.code.toLowerCase().includes(normalized)
    )
  }, [data.agencies, query])

  return (
    <div className="mx-auto max-w-[1350px]">
      <WorkspacePageHeader
        kicker="Access and allocation"
        title="Registered agencies"
        description="A representative administration view for agency membership, open requests, and synthetic download allocation."
      />

      <section className="mt-6 overflow-hidden rounded-[14px] border border-border bg-white">
        <div className="flex flex-col justify-between gap-4 border-b border-border p-4 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-[380px]">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search agency or code"
              aria-label="Search registered agencies"
              className="h-11 rounded-[10px] border-border bg-white pr-9"
            />
            <Search className="pointer-events-none absolute top-3 right-3 size-4 text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground">
            {agencies.length} shown · {data.totals.agencies} synthetic agencies
            in programme summary
          </p>
        </div>

        <div className="divide-y divide-border">
          {agencies.map((agency) => (
            <article
              key={agency.id}
              className="grid gap-5 p-5 sm:p-6 lg:grid-cols-[1fr_150px_170px_220px] lg:items-center"
            >
              <div className="flex min-w-0 gap-4">
                <span className="grid size-11 shrink-0 place-items-center rounded-[10px] bg-[#e8eef0] text-[var(--navy)]">
                  <Building2 className="size-5" />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold text-[var(--navy)]">
                      {agency.name}
                    </h2>
                    <Badge
                      variant="outline"
                      className={
                        agency.status === "Active"
                          ? "border-[#b8dcc8] bg-[#eaf6ef] text-[#246947]"
                          : "border-[#efd798] bg-[#fff7df] text-[#644b0d]"
                      }
                    >
                      <span className="size-1.5 rounded-full bg-current opacity-75" />
                      {agency.status}
                    </Badge>
                  </div>
                  <p className="mt-1 font-mono text-[0.65rem] text-muted-foreground">
                    {agency.code}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <Users className="size-4 text-[var(--service-blue)]" />
                <span>
                  <span className="block font-semibold text-[var(--ink)]">
                    {agency.members} members
                  </span>
                  <span className="mt-0.5 block text-muted-foreground">
                    Active identities
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <FolderKanban className="size-4 text-[var(--service-blue)]" />
                <span>
                  <span className="block font-semibold text-[var(--ink)]">
                    {agency.openOrders} open orders
                  </span>
                  <span className="mt-0.5 block text-muted-foreground">
                    Current workflow
                  </span>
                </span>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 text-[0.68rem]">
                  <span className="font-semibold text-[var(--ink)]">
                    Allocation used
                  </span>
                  <span className="text-muted-foreground">
                    {agency.allocationUsedPercent}%
                  </span>
                </div>
                <Progress
                  value={agency.allocationUsedPercent}
                  className="mt-2 h-1.5"
                  aria-label={`${agency.name} allocation used`}
                />
              </div>
            </article>
          ))}
        </div>

        {agencies.length === 0 ? (
          <div className="px-6 py-20 text-center">
            <Building2 className="mx-auto size-8 text-[var(--telemetry)]" />
            <h2 className="mt-4 font-semibold text-[var(--navy)]">
              No agencies match
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Search by another synthetic name or code.
            </p>
          </div>
        ) : null}
      </section>

      <div className="mt-6 flex items-start gap-3 rounded-[12px] border border-[#b8dbe1] bg-[#eaf7f8] p-4 text-[#164b59]">
        <CircleCheck className="mt-0.5 size-4 shrink-0" />
        <p className="text-xs leading-5">
          Agency records are illustrative and intentionally avoid names,
          contacts, and allocation values from the production database.
        </p>
      </div>
    </div>
  )
}
